"use client"

import { useState } from "react"
import type { Task, TimesheetEntry } from "@/lib/types"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Input } from "@/components/ui/input"
import { calculateStats, formatDuration } from "@/lib/time-utils"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts/lib/index.js"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Search } from "lucide-react"

interface StatsViewProps {
  timesheets: TimesheetEntry[]
  tasks: Task[]
}

const COLORS = ["#3b82f6", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981"]

export function StatsView({ timesheets, tasks }: StatsViewProps) {
  const [view, setView] = useState<"task" | "day" | "week" | "month">("task")
  const [selectedTaskId, setSelectedTaskId] = useState<string>("all")
  const [searchTerm, setSearchTerm] = useState("")
  const [taskSearchTerm, setTaskSearchTerm] = useState("")

  // Filter timesheets by task selection and search term
  const filteredTimesheets = timesheets.filter((entry) => {
    // Filter by selected task
    if (selectedTaskId !== "all" && entry.taskId !== selectedTaskId) return false
    
    // Filter by search term
    if (searchTerm.trim()) {
      const task = tasks.find((t) => t.id === entry.taskId)
      const matchesSearch = (task?.title?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                           (task?.description?.toLowerCase() || '').includes(searchTerm.toLowerCase())
      if (!matchesSearch) return false
    }
    
    return true
  })

  const stats = calculateStats(filteredTimesheets)

  const getTaskName = (taskId: string) => {
    return tasks.find((t) => t.id === taskId)?.title || "Unknown Task"
  }

  const taskData = Object.entries(stats.byTask).map(([taskId, minutes]) => ({
    name: getTaskName(taskId),
    minutes,
    hours: (minutes / 60).toFixed(1),
  }))

  const dayData = Object.entries(stats.byDay).map(([date, minutes]) => ({
    name: new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    minutes,
    hours: (minutes / 60).toFixed(1),
  }))

  const weekData = Object.entries(stats.byWeek).map(([week, minutes]) => ({
    name: week,
    minutes,
    hours: (minutes / 60).toFixed(1),
  }))

  const monthData = Object.entries(stats.byMonth).map(([month, minutes]) => ({
    name: new Date(month + "-01").toLocaleDateString("en-US", { month: "short", year: "numeric" }),
    minutes,
    hours: (minutes / 60).toFixed(1),
  }))

  const getCurrentData = () => {
    switch (view) {
      case "task":
        return taskData
      case "day":
        return dayData
      case "week":
        return weekData
      case "month":
        return monthData
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground dark:text-gray-100 mb-1">Statistics</h2>
          <p className="text-muted-foreground dark:text-gray-400">Analyze your time tracking data</p>
        </div>
        <div className="w-64">
          <Label>Filter by Task</Label>
          <Select value={selectedTaskId} onValueChange={setSelectedTaskId}>
            <SelectTrigger>
              <SelectValue placeholder="Select a task" />
            </SelectTrigger>
            <SelectContent>
              <div className="flex items-center border-b px-3 pb-2">
                <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                <input
                  placeholder="Search tasks..."
                  className="flex h-10 w-full rounded-md bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  value={taskSearchTerm}
                  onChange={(e) => setTaskSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
              <div className="max-h-[300px] overflow-y-auto">
                <SelectItem value="all">All Tasks</SelectItem>
                {tasks
                  .filter((task) =>
                    task.title.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
                    (task.description && task.description.toLowerCase().includes(taskSearchTerm.toLowerCase()))
                  )
                  .map((task) => (
                    <SelectItem key={task.id} value={task.id}>
                      {task.title}
                    </SelectItem>
                  ))}
                {tasks.filter((task) =>
                  task.title.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
                  (task.description && task.description.toLowerCase().includes(taskSearchTerm.toLowerCase()))
                ).length === 0 && taskSearchTerm && (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No tasks found
                  </div>
                )}
              </div>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Search Input */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search tasks in stats..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Time Tracked</CardDescription>
            <CardTitle className="text-3xl">{formatDuration(stats.totalMinutes)}</CardTitle>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Entries</CardDescription>
            <CardTitle className="text-3xl">{filteredTimesheets.length}</CardTitle>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Active Tasks</CardDescription>
            <CardTitle className="text-3xl">{Object.keys(stats.byTask).length}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Tabs value={view} onValueChange={(v) => setView(v as any)}>
        <TabsList className="grid w-full max-w-md grid-cols-4">
          <TabsTrigger value="task">By Task</TabsTrigger>
          <TabsTrigger value="day">By Day</TabsTrigger>
          <TabsTrigger value="week">By Week</TabsTrigger>
          <TabsTrigger value="month">By Month</TabsTrigger>
        </TabsList>

        <TabsContent value={view} className="space-y-4">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Bar Chart</CardTitle>
                <CardDescription>Time distribution overview</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={getCurrentData()}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="name" className="text-xs" />
                    <YAxis className="text-xs" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                        padding: "8px 12px",
                      }}
                      labelStyle={{
                        color: "hsl(var(--popover-foreground))",
                        fontWeight: 600,
                        marginBottom: "4px",
                      }}
                      itemStyle={{
                        color: "hsl(var(--popover-foreground))",
                      }}
                      formatter={(value: number) => [`${formatDuration(value)}`, "Duration"]}
                    />
                    <Bar dataKey="minutes" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Pie Chart</CardTitle>
                <CardDescription>Proportional breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={getCurrentData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry: { name: string }) => entry.name}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="minutes"
                    >
                      {getCurrentData().map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                        padding: "8px 12px",
                      }}
                      labelStyle={{
                        color: "hsl(var(--popover-foreground))",
                        fontWeight: 600,
                      }}
                      itemStyle={{
                        color: "hsl(var(--popover-foreground))",
                      }}
                      formatter={(value: number) => formatDuration(value)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Detailed Breakdown</CardTitle>
            </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {getCurrentData().map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between py-2 border-b border-border dark:border-gray-700 last:border-0"
                    >
                      <span className="font-medium text-foreground dark:text-gray-100">{item.name}</span>
                      <span className="text-muted-foreground dark:text-gray-400">{formatDuration(item.minutes)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    )
  }